<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateMeter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const item = ref(null);
  const formName = ref('meter');
  const formId = ref(null);
  const submitAndClose = ref(true);
  const container = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    submitAndClose: submitAndClose.value,
    container: container.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateMeter', { multiplier: 1 });
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_container = () => {
    return {
      self: $fn.value.urlFor('create_meter'),
      object: 'list',
      data: [],
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'submitAndClose' },
      { name: 'container' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      container: $$load_container,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      submitAndClose,
      container,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Meter') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_wgwzeq1bi_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_p6fkd1isp_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_qc3b568eo_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.meter',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadGrid_hptv73ypb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'meters',
    };
  };
  const act_1040167445267876_rest_rm6o783jp_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: item.value,
    };
  };
  const act_2434514588044777_script_r9gfx181k_value_callable = async (
    $event
  ) => {
    return (
      setData(
        'item',
        item.value.clone({
          id: null,
          rev: null,
          serial_number: null,
          links: null,
          etag: null,
          locked: null,
        })
      ),
      setData('editor', item.value.editor())
    );
  };
  const act_2434514588044777_script_r9gfx181k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2434514588044777_script_r9gfx181k_value_callable($event);
      },
    };
  };
  const act_2434514588044777_reloadGrid_ms3rik3h0_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'meters',
    };
  };
  const act_2434514588044777_rest_jfwm2wclq_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: item.value,
    };
  };

  const act_7315092382398562_closeModal_wgwzeq1bi =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_wgwzeq1bi_getActionArgs,
    });
  const inst_evh_7315092382398562_close_3ciqkvrut =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_wgwzeq1bi,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_3ciqkvrut],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_4uh59mkvu =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_w01oslfhn =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4uh59mkvu,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_w01oslfhn],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_i7eu4bbzn =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_t015i4q6b =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_i7eu4bbzn,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_t015i4q6b],
    event: 'close',
  });
  const act_1040167445267876_closeModal_p6fkd1isp =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_p6fkd1isp_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_g5mn0jakl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_p6fkd1isp,
      event: 'saved',
      displayName: 'closeModal',
    });
  const act_1040167445267876_navigate_qc3b568eo =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_qc3b568eo_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_hi0l9e9ge =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_qc3b568eo,
      event: 'saved',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadGrid_hptv73ypb =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_hptv73ypb_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_l8lv3zs9s =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_hptv73ypb,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_rest_rm6o783jp = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_rm6o783jp_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_saved_g5mn0jakl,
      inst_evh_1040167445267876_saved_hi0l9e9ge,
      inst_evh_1040167445267876_saved_l8lv3zs9s,
    ],
  });
  const inst_evh_1040167445267876_submit_mbomxjtms =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_rm6o783jp,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_mbomxjtms],
    event: 'submit',
  });
  const act_2434514588044777_script_r9gfx181k = new actionTypes.ScriptAction({
    actionArgs: act_2434514588044777_script_r9gfx181k_getActionArgs,
  });
  const inst_evh_2434514588044777_saved_78nsvb3of =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_script_r9gfx181k,
      event: 'saved',
      displayName: 'script',
    });
  const act_2434514588044777_reloadGrid_ms3rik3h0 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2434514588044777_reloadGrid_ms3rik3h0_getActionArgs,
    });
  const inst_evh_2434514588044777_saved_uoopdsc00 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_reloadGrid_ms3rik3h0,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_2434514588044777_rest_jfwm2wclq = new actionTypes.RestAction({
    actionArgs: act_2434514588044777_rest_jfwm2wclq_getActionArgs,
    displayName: 'create2',
    events: [
      inst_evh_2434514588044777_saved_78nsvb3of,
      inst_evh_2434514588044777_saved_uoopdsc00,
    ],
  });
  const inst_evh_2434514588044777_click_zp03ccbrp =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_rest_jfwm2wclq,
      event: 'click',
      displayName: 'create2',
    });
  const inst_evh_2434514588044777_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2434514588044777_click_zp03ccbrp],
    event: 'click',
  });
  const act_2167826738365259_closeComponent_fj4b2ywh8 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2167826738365259_clickPreventStop_t097cyl2f =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeComponent_fj4b2ywh8,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_t097cyl2f],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_3ciqkvrut,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_w01oslfhn,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_t015i4q6b,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_g5mn0jakl,
    inst_evh_1040167445267876_saved_hi0l9e9ge,
    inst_evh_1040167445267876_saved_l8lv3zs9s,
    inst_evh_1040167445267876_submit_mbomxjtms,
    create: inst_evh_1040167445267876_submit_mbomxjtms,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_saved_78nsvb3of,
    inst_evh_2434514588044777_saved_uoopdsc00,
    inst_evh_2434514588044777_click_zp03ccbrp,
    create2: inst_evh_2434514588044777_click_zp03ccbrp,
    inst_evh_2434514588044777_click,
    inst_evh_2167826738365259_clickPreventStop_t097cyl2f,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_3ciqkvrut = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_3ciqkvrut.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_w01oslfhn = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_w01oslfhn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_t015i4q6b = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_t015i4q6b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_g5mn0jakl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_g5mn0jakl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_hi0l9e9ge = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_hi0l9e9ge.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_l8lv3zs9s = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_l8lv3zs9s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_mbomxjtms = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_mbomxjtms.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_saved_78nsvb3of = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_saved_78nsvb3of.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_saved_uoopdsc00 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_saved_uoopdsc00.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_click_zp03ccbrp = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_click_zp03ccbrp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_click = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_t097cyl2f = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_t097cyl2f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateMeter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateMeter"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetMeterGeneralData :meter="item"> </FieldsetMeterGeneralData>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.create.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="
            pgettext('LetterRegisterEntry/Command', 'Save and create another')
          "
          :disabled="
            !forms[formName] ||
            !forms[formName].submittable ||
            (actions.create && actions.create.isRunning)
          "
          variant="light"
          icon="mdi:forward"
          class="me-2"
          @click="evh_2434514588044777_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
